import React, { useState } from 'react';
import { ShieldCheckIcon, SearchIcon, AlertCircleIcon, CheckCircleIcon, ArrowLeftIcon, GlobeIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { samplePlots } from '../data/sampleData';
export function CLOVerificationPage({
  navigateTo
}: {
  navigateTo?: (page: string) => void;
}) {
  const {
    language,
    toggleLanguage,
    t
  } = useLanguage();
  const [cloId, setCloId] = useState('');
  const [result, setResult] = useState<any>(null);
  const [searched, setSearched] = useState(false);
  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    setSearched(true);
    const found = samplePlots.find(p => p.cloId === cloId);
    setResult(found || null);
  };
  return <div className="min-h-screen bg-[#F8FAFB] flex flex-col font-sans" data-id="element-174">
      {/* Public Header */}
      <header className="bg-[#006A4E] text-white h-14 flex items-center justify-between px-4 md:px-8 flex-shrink-0 shadow-md" data-id="element-175">
        <div className="flex items-center space-x-4" data-id="element-176">
          {navigateTo && <button onClick={() => navigateTo('home')} className="flex items-center text-green-100 hover:text-white transition-colors" data-id="element-177">
              <ArrowLeftIcon className="w-5 h-5 mr-1" data-id="element-178" />
              <span className="hidden sm:inline font-medium" data-id="element-179">
                {t('publicHome')}
              </span>
            </button>}
          <div className="h-6 w-px bg-green-700 hidden sm:block" data-id="element-180"></div>
          <div className="flex items-center space-x-2" data-id="element-181">
            <ShieldCheckIcon className="w-5 h-5 text-[#C9A84C]" data-id="element-182" />
            <span className="font-bold text-lg" data-id="element-183">{t('verifyCLO')}</span>
          </div>
        </div>
        <button onClick={toggleLanguage} className="flex items-center space-x-1 text-sm font-medium text-green-100 hover:text-white transition-colors" data-id="element-184">
          <GlobeIcon className="w-4 h-4" data-id="element-185" />
          <span data-id="element-186">{language === 'bn' ? 'English' : 'বাংলা'}</span>
        </button>
      </header>

      <div className="flex-1 flex items-start justify-center py-12 px-4" data-id="element-187">
        <div className="max-w-3xl w-full space-y-8" data-id="element-188">
          <div className="text-center space-y-4" data-id="element-189">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto border-2 border-green-200" data-id="element-190">
              <ShieldCheckIcon className="w-10 h-10 text-[#006A4E]" data-id="element-191" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900" data-id="element-192">
              {language === 'bn' ? 'সিএলও সনদ যাচাই' : 'Verify CLO Certificate'}
            </h1>
            <p className="text-gray-500" data-id="element-193">
              {language === 'bn' ? 'সনদের সত্যতা যাচাই করতে সার্টিফিকেট আইডি দিন অথবা QR কোড স্ক্যান করুন।' : 'Enter the Certificate ID or scan the QR code to verify authenticity.'}
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-id="element-194">
            <form onSubmit={handleVerify} className="flex flex-col sm:flex-row gap-4" data-id="element-195">
              <div className="flex-1 relative" data-id="element-196">
                <SearchIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-197" />
                <input type="text" value={cloId} onChange={e => setCloId(e.target.value)} placeholder="e.g. CLO-GAZ-2026-000145" className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-[#006A4E] focus:border-[#006A4E]" required data-id="element-198" />
              </div>
              <button type="submit" className="px-6 py-3 bg-[#006A4E] text-white rounded-lg font-medium hover:bg-[#004D3A] transition-colors shadow-sm" data-id="element-199">
                {t('verify')}
              </button>
            </form>
          </div>

          {searched && <div className={`p-6 rounded-xl border ${result ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-id="element-200">
              {result ? <div className="space-y-4" data-id="element-201">
                  <div className="flex items-center space-x-3 text-green-700 mb-6 border-b border-green-200 pb-4" data-id="element-202">
                    <CheckCircleIcon className="w-8 h-8" data-id="element-203" />
                    <div data-id="element-204">
                      <h3 className="text-xl font-bold" data-id="element-205">
                        {language === 'bn' ? 'বৈধ সনদ' : 'Valid Certificate'}
                      </h3>
                      <p className="text-sm opacity-90" data-id="element-206">
                        {language === 'bn' ? 'এই সিএলও সনদটি সত্য এবং বর্তমানে সক্রিয়।' : 'This CLO is authentic and currently active.'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-id="element-207">
                    <div data-id="element-208">
                      <p className="text-gray-500 mb-1" data-id="element-209">{t('plotName')}</p>
                      <p className="font-bold text-gray-900" data-id="element-210">
                        {language === 'bn' ? result.nameBn : result.name}
                      </p>
                    </div>
                    <div data-id="element-211">
                      <p className="text-gray-500 mb-1" data-id="element-212">{t('locationInfo')}</p>
                      <p className="font-bold text-gray-900" data-id="element-213">
                        {language === 'bn' ? result.districtBn : result.district}
                        ,{' '}
                        {language === 'bn' ? result.upazilaBn : result.upazila}
                      </p>
                    </div>
                    <div data-id="element-214">
                      <p className="text-gray-500 mb-1" data-id="element-215">
                        {t('khatian')} / {t('dag')}
                      </p>
                      <p className="font-bold text-gray-900" data-id="element-216">
                        {result.khatian} / {result.dag}
                      </p>
                    </div>
                    <div data-id="element-217">
                      <p className="text-gray-500 mb-1" data-id="element-218">{t('area')}</p>
                      <p className="font-bold text-gray-900" data-id="element-219">
                        {result.area} {language === 'bn' ? 'শতাংশ' : 'Decimals'}
                      </p>
                    </div>
                    <div data-id="element-220">
                      <p className="text-gray-500 mb-1" data-id="element-221">{t('landType')}</p>
                      <p className="font-bold text-gray-900 capitalize" data-id="element-222">
                        {t(result.type as any)}
                      </p>
                    </div>
                    <div data-id="element-223">
                      <p className="text-gray-500 mb-1" data-id="element-224">{t('ownershipType')}</p>
                      <p className="font-bold text-gray-900 capitalize" data-id="element-225">
                        {t(result.ownership as any)}
                      </p>
                    </div>
                  </div>
                </div> : <div className="flex items-center space-x-3 text-red-700" data-id="element-226">
                  <AlertCircleIcon className="w-8 h-8" data-id="element-227" />
                  <div data-id="element-228">
                    <h3 className="text-xl font-bold" data-id="element-229">
                      {language === 'bn' ? 'অবৈধ সনদ' : 'Invalid Certificate'}
                    </h3>
                    <p className="text-sm opacity-90" data-id="element-230">
                      {language === 'bn' ? 'প্রদত্ত সার্টিফিকেট আইডির জন্য কোনো রেকর্ড পাওয়া যায়নি।' : 'No record found for the provided Certificate ID.'}
                    </p>
                  </div>
                </div>}
            </div>}
        </div>
      </div>
    </div>;
}